import React, { useState, useMemo } from 'react';
import {
  Box, TextField, Button, Grid, FormControl,
  InputLabel, Select, MenuItem, Typography, Divider,
  ToggleButton, ToggleButtonGroup, Checkbox, FormControlLabel,
  FormGroup, FormLabel
} from '@mui/material';
import { toast } from 'react-toastify';

const PLANES_SAAS = [
  { label: 'Comercial Starter', precio: 60 },
  { label: 'Comercial Pro',     precio: 120 },
  { label: 'Comercial Scale',   precio: 180 },
  { label: 'Constructor Starter', precio: 99 },
  { label: 'Constructor Pro',     precio: 150 },
  { label: 'Constructor Scale',   precio: 199 },
  { label: 'Contable Starter',  precio: 60 },
  { label: 'Contable Pro',      precio: 99 },
  { label: 'Contable Scale',    precio: 149 },
  { label: 'Renta-car Starter', precio: 89 },
  { label: 'Renta-car Pro',     precio: 130 },
  { label: 'Renta-car Scale',   precio: 179 },
];

const MODULOS_SAAS = [
  'Facturación', 'Contabilidad', 'Nómina', 'POS', 'CRM', 'Renta-car', 'e-CF'
];

const ADDONS_SAAS = [
  { label: 'e-CF',             codigo: 'ecf',             precio: 30 },
  { label: 'CRM',              codigo: 'crm',             precio: 15 },
  { label: 'Multi-empresa',    codigo: 'multi_empresa',   precio: 25 },
  { label: 'Email Marketing',  codigo: 'email_marketing', precio: 30 },
];

const NecesidadValidadaForm = ({ oportunidad, onGuardado, onTransicionCompleta }) => {
  const [formData, setFormData] = useState({
    // ── Clasificación flexible ───────────────────────────────────────
    tipo_negocio: oportunidad?.tipo_negocio || 'obra_civil',
    tipo_oportunidad: oportunidad?.necesidad_validada?.tipo_oportunidad ||
                      oportunidad?.necesidad_detalle?.tipo_oportunidad || '',

    // ── Datos comunes ────────────────────────────────────────────────
    ubicacion_proyecto: oportunidad?.necesidad_validada?.ubicacion ||
                        oportunidad?.necesidad_detalle?.ubicacion_proyecto || '',
    presupuesto_referencial: oportunidad?.valor_estimado ||
                             oportunidad?.necesidad_detalle?.presupuesto_referencial || '',
    fecha_estimada_decision: oportunidad?.necesidad_validada?.fecha_estimada_decision ||
                             oportunidad?.necesidad_detalle?.fecha_estimada_decision || '',

    // ── Campos obra_civil ────────────────────────────────────────────
    visita_tecnica_realizada: oportunidad?.necesidad_validada?.visita_tecnica_realizada || false,
    tipo_obra: oportunidad?.necesidad_validada?.tipo_obra || '',
    plazo_estimado: oportunidad?.necesidad_validada?.plazo_estimado || '',

    // ── Campos servicios ─────────────────────────────────────────────
    tipo_servicio: oportunidad?.necesidad_validada?.tipo_servicio || '',
    alcance: oportunidad?.necesidad_validada?.alcance || '',
    entregables: oportunidad?.necesidad_validada?.entregables || '',
    duracion_estimada: oportunidad?.necesidad_validada?.duracion_estimada || '',

    // ── Campos SaaS ──────────────────────────────────────────────────
    plan_interes: oportunidad?.necesidad_validada?.plan_interes || '',
    num_usuarios: oportunidad?.necesidad_validada?.num_usuarios || '',
    modulos_interes: oportunidad?.necesidad_validada?.modulos_interes || [],
    addons_interes: oportunidad?.necesidad_validada?.addons_interes || [],

    notas_diagnostico: oportunidad?.necesidad_validada?.notas ||
                       oportunidad?.necesidad_detalle?.notas_diagnostico || ''
  });

  const handleTipoNegocio = (event, valor) => {
    if (valor !== null) {
      setFormData(prev => ({ ...prev, tipo_negocio: valor, tipo_oportunidad: '' }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePlanChange = (e) => {
    setFormData(prev => ({ ...prev, plan_interes: e.target.value }));
  };

  const handleModuloToggle = (modulo) => {
    setFormData(prev => {
      const lista = prev.modulos_interes.includes(modulo)
        ? prev.modulos_interes.filter(m => m !== modulo)
        : [...prev.modulos_interes, modulo];
      return { ...prev, modulos_interes: lista };
    });
  };

  const handleAddonToggle = (codigo) => {
    setFormData(prev => {
      const lista = prev.addons_interes.includes(codigo)
        ? prev.addons_interes.filter(a => a !== codigo)
        : [...prev.addons_interes, codigo];
      return { ...prev, addons_interes: lista };
    });
  };

  const presupuestoMensual = useMemo(() => {
    const planObj = PLANES_SAAS.find(p => p.label === formData.plan_interes);
    const base = planObj?.precio || 0;
    const addonTotal = ADDONS_SAAS
      .filter(a => formData.addons_interes.includes(a.codigo))
      .reduce((sum, a) => sum + a.precio, 0);
    return base + addonTotal;
  }, [formData.plan_interes, formData.addons_interes]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const esSaasLocal = formData.tipo_negocio === 'saas';

    if (!esSaasLocal && !formData.tipo_oportunidad.trim()) {
      toast.error('Indica el tipo de oportunidad (ej: Obra Civil, Consultoría, Venta de productos)');
      return;
    }

    if (esSaasLocal && !formData.plan_interes) {
      toast.error('Selecciona el plan de interés');
      return;
    }

    try {
      let payload;

      if (esSaasLocal) {
        payload = {
          tipo_negocio: 'saas',
          tipo_oportunidad: formData.plan_interes,
          plan_interes: formData.plan_interes,
          num_usuarios: formData.num_usuarios,
          modulos_interes: formData.modulos_interes,
          addons_interes: formData.addons_interes,
          presupuesto_mensual: presupuestoMensual,
          fecha_estimada_decision: formData.fecha_estimada_decision,
          notas: formData.notas_diagnostico
        };
      } else if (formData.tipo_negocio === 'obra_civil') {
        payload = {
          tipo_negocio: formData.tipo_negocio,
          tipo_oportunidad: formData.tipo_oportunidad,
          visita_tecnica_realizada: formData.visita_tecnica_realizada,
          tipo_obra: formData.tipo_oportunidad,
          ubicacion: formData.ubicacion_proyecto,
          plazo_estimado: formData.plazo_estimado,
          fecha_estimada_decision: formData.fecha_estimada_decision,
          notas: formData.notas_diagnostico
        };
      } else {
        payload = {
          tipo_negocio: formData.tipo_negocio,
          tipo_oportunidad: formData.tipo_oportunidad,
          tipo_servicio: formData.tipo_oportunidad,
          alcance: formData.alcance,
          entregables: formData.entregables,
          duracion_estimada: formData.duracion_estimada,
          fecha_estimada_decision: formData.fecha_estimada_decision,
          notas: formData.notas_diagnostico
        };
      }

      await onGuardado(payload);
      toast.success('✅ Calificación técnica guardada');

      if (onTransicionCompleta) onTransicionCompleta();
    } catch (error) {
      console.error('Error al guardar validación:', error);
      toast.error('❌ Error al procesar los datos');
    }
  };

  const esObraCivil = formData.tipo_negocio === 'obra_civil';
  const esSaas = formData.tipo_negocio === 'saas';

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          🔍 Validación de Necesidad
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Diagnóstico técnico inicial para calificar la oportunidad
        </Typography>
      </Box>

      <Grid container spacing={2}>

        {/* ── Tipo de negocio ─────────────────────────────────────────── */}
        <Grid item xs={12} sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Tipo de negocio *
          </Typography>
          <ToggleButtonGroup
            value={formData.tipo_negocio}
            exclusive
            onChange={handleTipoNegocio}
            color="primary"
            fullWidth
            size="small"
          >
            <ToggleButton value="obra_civil" sx={{ fontWeight: 'bold' }}>
              🏗️ Obra Civil
            </ToggleButton>
            <ToggleButton value="servicios" sx={{ fontWeight: 'bold' }}>
              💼 Servicios / Productos
            </ToggleButton>
            <ToggleButton value="saas" sx={{ fontWeight: 'bold' }}>
              🚀 SaaS
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>

        {/* ══════════════════════════════════════════════════════════════
            SECCIÓN SaaS
           ══════════════════════════════════════════════════════════════ */}
        {esSaas && (
          <>
            {/* Plan de interés */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Plan de interés *</InputLabel>
                <Select
                  value={formData.plan_interes}
                  label="Plan de interés *"
                  onChange={handlePlanChange}
                >
                  {PLANES_SAAS.map(p => (
                    <MenuItem key={p.label} value={p.label}>
                      {p.label} — ${p.precio}/mes
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Número de usuarios */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Número de usuarios estimados"
                name="num_usuarios"
                type="number"
                inputProps={{ min: 1 }}
                value={formData.num_usuarios}
                onChange={handleChange}
              />
            </Grid>

            {/* Módulos de interés */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  Módulos de interés
                </FormLabel>
                <FormGroup row>
                  {MODULOS_SAAS.map(modulo => (
                    <FormControlLabel
                      key={modulo}
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.modulos_interes.includes(modulo)}
                          onChange={() => handleModuloToggle(modulo)}
                        />
                      }
                      label={modulo}
                      sx={{ mr: 2 }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Add-ons de interés */}
            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                  Add-ons de interés
                </FormLabel>
                <FormGroup row>
                  {ADDONS_SAAS.map(addon => (
                    <FormControlLabel
                      key={addon.codigo}
                      control={
                        <Checkbox
                          size="small"
                          checked={formData.addons_interes.includes(addon.codigo)}
                          onChange={() => handleAddonToggle(addon.codigo)}
                        />
                      }
                      label={`${addon.label} +$${addon.precio}/mes`}
                      sx={{ mr: 2 }}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </Grid>

            {/* Fecha de decisión + Presupuesto mensual */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Fecha estimada de decisión"
                name="fecha_estimada_decision"
                type="date"
                value={formData.fecha_estimada_decision}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Presupuesto mensual estimado"
                value={presupuestoMensual > 0 ? `$${presupuestoMensual}/mes` : '—'}
                InputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
                helperText={formData.plan_interes ? 'Calculado según plan y add-ons seleccionados' : 'Selecciona un plan primero'}
              />
            </Grid>

            {/* Notas de calificación */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas de calificación"
                name="notas_diagnostico"
                multiline rows={2}
                value={formData.notas_diagnostico}
                onChange={handleChange}
              />
            </Grid>
          </>
        )}

        {/* ══════════════════════════════════════════════════════════════
            SECCIÓN OBRA CIVIL / SERVICIOS (sin cambios)
           ══════════════════════════════════════════════════════════════ */}
        {!esSaas && (
          <>
            {/* Tipo de oportunidad (texto libre) */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth required size="small"
                label="Tipo de oportunidad"
                name="tipo_oportunidad"
                value={formData.tipo_oportunidad}
                onChange={handleChange}
                placeholder={esObraCivil
                  ? 'Ej: Edificio residencial, Planta fotovoltaica, Carretera...'
                  : 'Ej: Consultoría IT, Training, Venta de equipos...'}
                helperText="Describe el tipo de trabajo o servicio específico"
              />
            </Grid>

            {/* Fecha estimada de decisión */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Fecha estimada de decisión"
                name="fecha_estimada_decision"
                type="date"
                value={formData.fecha_estimada_decision}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Ubicación y presupuesto */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Ubicación / Sitio"
                name="ubicacion_proyecto"
                value={formData.ubicacion_proyecto}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth size="small"
                label="Presupuesto Referencial"
                name="presupuesto_referencial"
                type="number"
                value={formData.presupuesto_referencial}
                onChange={handleChange}
                InputProps={{ startAdornment: 'RD$ ' }}
              />
            </Grid>

            <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>

            {/* Campos específicos: OBRA CIVIL */}
            {esObraCivil && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Visita técnica realizada</InputLabel>
                    <Select
                      value={formData.visita_tecnica_realizada}
                      label="Visita técnica realizada"
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        visita_tecnica_realizada: e.target.value === 'true'
                      }))}
                    >
                      <MenuItem value={true}>Sí</MenuItem>
                      <MenuItem value={false}>No</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth size="small"
                    label="Plazo estimado (días/meses)"
                    name="plazo_estimado"
                    placeholder="Ej: 6 meses, 120 días..."
                    value={formData.plazo_estimado}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Uso / Tipo</InputLabel>
                    <Select
                      value={formData.tipo_obra}
                      label="Uso / Tipo"
                      onChange={(e) => setFormData(prev => ({ ...prev, tipo_obra: e.target.value }))}
                    >
                      <MenuItem value="residencial">Residencial</MenuItem>
                      <MenuItem value="comercial">Comercial</MenuItem>
                      <MenuItem value="industrial">Industrial</MenuItem>
                      <MenuItem value="infraestructura">Infraestructura</MenuItem>
                      <MenuItem value="fotovoltaica">Planta Fotovoltaica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Campos específicos: SERVICIOS */}
            {!esObraCivil && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small"
                    label="Tipo de servicio"
                    name="tipo_servicio"
                    placeholder="Ej: Consultoría, Soporte, Capacitación..."
                    value={formData.tipo_servicio}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth size="small"
                    label="Duración estimada"
                    name="duracion_estimada"
                    placeholder="Ej: 3 meses, 40 horas..."
                    value={formData.duracion_estimada}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Entregables esperados"
                    name="entregables"
                    placeholder="Ej: Informe final, Certificación, Instalación de equipos..."
                    value={formData.entregables}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Alcance del servicio"
                    name="alcance"
                    placeholder="Ej: 500 unidades, 10 empleados, cobertura nacional..."
                    value={formData.alcance}
                    onChange={handleChange}
                  />
                </Grid>
              </>
            )}

            {/* Notas generales */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas del diagnóstico"
                name="notas_diagnostico"
                multiline rows={2}
                value={formData.notas_diagnostico}
                onChange={handleChange}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 1,
              py: 1.5,
              fontWeight: 'bold',
              bgcolor: esObraCivil ? '#3b82f6' : esSaas ? '#06b6d4' : '#667eea',
              '&:hover': { bgcolor: esObraCivil ? '#2563eb' : esSaas ? '#0891b2' : '#5568d3' }
            }}
          >
            Validar y Avanzar
          </Button>
        </Grid>

      </Grid>
    </Box>
  );
};

export default NecesidadValidadaForm;
